import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  tmsApi,
  type CreateServiceTagRequest,
  type ServiceTag,
  type UpdateServiceTagRequest,
} from '../client';

/**
 * Stable query-key factory for service-tag-scoped caches.
 *
 * Keyed by `'tms'` + entity to avoid collisions with `@my/directory-api`
 * keys that may live in the same QueryClient. The list cache is keyed by
 * organisation id; the by-id cache by service-tag id.
 */
export const serviceTagKeys = {
  all: ['tms', 'service-tag'] as const,
  byOrganisation: (organisationId: string) =>
    ['tms', 'service-tag', 'org', organisationId] as const,
  byId: (id: string) => ['tms', 'service-tag', 'id', id] as const,
};

/**
 * Fetch every service tag for an organisation. Pass `null` to keep the
 * query disabled (e.g. while the org id is still resolving from Auth0).
 */
export function useServiceTags(
  organisationId: string | null,
  options?: Omit<UseQueryOptions<ServiceTag[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<ServiceTag[], Error>({
    queryKey: organisationId ? serviceTagKeys.byOrganisation(organisationId) : serviceTagKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return tmsApi.getServiceTags(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/** Fetch a single service tag by id. Pass `null` to keep the query disabled. */
export function useServiceTag(
  id: string | null,
  options?: Omit<UseQueryOptions<ServiceTag, Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<ServiceTag, Error>({
    queryKey: id ? serviceTagKeys.byId(id) : serviceTagKeys.all,
    queryFn: () => {
      if (!id) throw new Error('No service tag id');
      return tmsApi.getServiceTag(id);
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Mutation wrapper around POST `/tms/api/v1/serviceTags`. Invalidates the
 * per-org list cache on success so the new row shows up.
 */
export function useCreateServiceTag() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateServiceTagRequest>({
    mutationKey: [...serviceTagKeys.all, 'create'],
    mutationFn: (body) => tmsApi.createServiceTag(body),
    onSuccess: (_id, vars) => {
      qc.invalidateQueries({
        queryKey: serviceTagKeys.byOrganisation(vars.organisationId),
      });
    },
  });
}

/**
 * Mutation wrapper around PUT `/tms/api/v1/serviceTags`. The caller passes
 * `organisationId` alongside the body so we can invalidate the per-org list
 * cache without an extra round-trip — the wire body itself only carries the
 * service tag id.
 */
export function useUpdateServiceTag() {
  const qc = useQueryClient();
  return useMutation<string, Error, { organisationId: string; body: UpdateServiceTagRequest }>({
    mutationKey: [...serviceTagKeys.all, 'update'],
    mutationFn: ({ body }) => tmsApi.updateServiceTag(body),
    onSuccess: (_id, { organisationId, body }) => {
      qc.invalidateQueries({ queryKey: serviceTagKeys.byOrganisation(organisationId) });
      qc.invalidateQueries({ queryKey: serviceTagKeys.byId(body.serviceTagId) });
    },
  });
}

/**
 * Mutation wrapper around DELETE `/tms/api/v1/serviceTags/{id}`. Caller
 * passes `organisationId` for cache-key targeting (same reason as update).
 */
export function useDeleteServiceTag() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { organisationId: string; id: string }>({
    mutationKey: [...serviceTagKeys.all, 'delete'],
    mutationFn: ({ id }) => tmsApi.deleteServiceTag(id),
    onSuccess: (_res, { organisationId, id }) => {
      qc.invalidateQueries({ queryKey: serviceTagKeys.byOrganisation(organisationId) });
      qc.removeQueries({ queryKey: serviceTagKeys.byId(id) });
    },
  });
}
